import enTranslations from '@myosh/odin-translations/translations/en.json';
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { localeLoaders, matchApiLanguageToTranslationFile } from './supported-languages';

/** Lazy-loads the translation bundle for a language, falling back to `en` then `{}`. */
export const loadTranslation = async (language: string) => {
  const loader = localeLoaders[language] || localeLoaders.en;
  try {
    const module = await loader();
    return module.default;
  } catch {
    return {};
  }
};

/** Statically bundled `en` resources so the first paint has translations available. */
export const resources = {
  en: {
    translation: enTranslations,
  },
};

const STORAGE_KEY = 'vikingUserLocale';

i18next
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    lng: localStorage.getItem(STORAGE_KEY) || 'en',
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    debug: false,
    react: { useSuspense: false },
    resources: resources,
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: STORAGE_KEY,
      caches: ['localStorage'],
      excludeCacheFor: ['cimode'],
    },
    showSupportNotice: false,
  });

// Handle language changes and loads translation file for the new language
i18next.on('languageChanged', async (lng) => {
  const translations = await loadTranslation(lng);
  i18next.addResourceBundle(lng, 'translation', translations, true, true);
});

/** Persists and applies the user's locale, normalising it to a supported language first. */
export const setUserLocale = (localeCode: string) => {
  const language = matchApiLanguageToTranslationFile(localeCode);
  localStorage.setItem(STORAGE_KEY, language);
  i18next.changeLanguage(language);
};

/**
 * Bound to the configured instance so non-component modules can import `t` from
 * this shared config rather than the bare `i18next` package, guaranteeing
 * initialization has run.
 */
export const t = i18next.t.bind(i18next);

export { i18next };
